import React from "react";
import { Pencil, Trash2 } from "lucide-react";

import { Button, Modal } from "../../components/ui";
import { DEFAULT_ASSESSMENT_FORM_UI_CONFIG } from "../../utils/assessmentFormConfig";
import {
  normalizeAssessmentFieldsFromApi,
  parseMaturityOptionsJson,
} from "../../utils/subCapabilityAssessmentConfig";
import { parseFrameworkFileNames, parseFrameworkUrls } from "../../services/frameworkApi";
import { hasNamedSubCapabilities } from "../../components/sa/CapabilityForm/capabilityFormShared";

type Props = {
  open: boolean;
  capability: any;
  frameworkLabel: string;
  framework?: any;
  sourceTypeName?: string;
  userTypeLabel: string;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

const activeQuestions = (questions: any[] | undefined) =>
  (questions || []).filter((q) => q?.questionText?.trim());

const Questions = ({ title, questions }: { title: string; questions: any[] | undefined }) => {
  const items = activeQuestions(questions);
  return (
    <section className="ab-view__card cv-view__section">
      <h3 className="cv-view__title">
        {title} <span className="cv-view__count">{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <p className="ab-wizard__hint">No questions.</p>
      ) : (
        <ol className="ab-view__questions">
          {items.map((q, i) => (
            <li key={q.id || i}>
              <span className="ab-view__qnum">{i + 1}</span>
              <span>{q.questionText}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export const Placeholders = ({ unit }: { unit: any }) => {
  const items = [
    { label: "Current state", text: unit?.currentStateCommentPlaceholder?.trim() },
    { label: "Future state", text: unit?.futureStateCommentPlaceholder?.trim() },
  ].filter((i) => i.text);
  if (items.length === 0) return null;
  return (
    <section className="ab-view__card cv-view__section">
      <h3 className="cv-view__title">Comment placeholders</h3>
      <div className="cv-view__scale cv-view__scale--wide">
        {items.map((i) => (
          <div key={i.label} className="cv-view__scale-item">
            <strong>{i.label}</strong>
            <small>{i.text}</small>
          </div>
        ))}
      </div>
    </section>
  );
};

export const Scale = ({ options, custom }: { options: any[] | undefined; custom: boolean }) => (
  <section className="ab-view__card cv-view__section">
    <h3 className="cv-view__title">
      Maturity scale{" "}
      <span className={custom ? "ab-wizard__pill ab-wizard__pill--accent" : "ab-wizard__pill"}>
        {custom ? "Custom" : "Default"}
      </span>
    </h3>
    {options && options.length > 0 ? (
      <div className="cv-view__scale">
        {options.map((o, i) => (
          <div key={`${o.optionValue ?? i}-${o.label}`} className="cv-view__scale-item">
            <span className="ab-view__qnum">{o.optionValue ?? i + 1}</span>
            <strong>{o.label || "—"}</strong>
            <small>{o.definition?.trim() || "No definition"}</small>
          </div>
        ))}
      </div>
    ) : (
      <p className="ab-wizard__hint">None.</p>
    )}
  </section>
);

const formatDate = (value: any) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

export const SourcePanel = ({ label, framework, sourceTypeName }: { label: string; framework?: any; sourceTypeName?: string }) => {
  const files: string[] = framework ? parseFrameworkFileNames(framework.attachmentPath) : [];
  const urls: string[] = framework ? parseFrameworkUrls(framework.urls) : [];
  const rows: [string, string][] = [
    ["Source type", sourceTypeName || framework?.frameworkType || "—"],
    ["Ref. company", framework?.referenceCompanyName?.trim() || "—"],
    ["Imported", formatDate(framework?.createdAt)],
  ];
  return (
    <section className="ab-view__card ab-view__panel">
      <h3>Source document</h3>
      <p className="cv-view__src-name">{label || "—"}</p>
      {framework?.description?.trim() ? <p className="ab-view__about">{framework.description.trim()}</p> : null}
      <dl className="cv-view__facts">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {files.length > 0 || urls.length > 0 ? (
        <ul className="cv-view__links">
          {files.map((f) => (
            <li key={f}>{f}</li>
          ))}
          {urls.map((u) => (
            <li key={u}>
              <a href={u} target="_blank" rel="noopener noreferrer">
                {u}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export const CapabilityViewModal: React.FC<Props> = ({
  open,
  capability,
  frameworkLabel,
  framework,
  sourceTypeName,
  userTypeLabel,
  onClose,
  onEdit,
  onDelete,
}) => {
  const subs: any[] = capability?.subCapabilities || [];
  const hasSubs = hasNamedSubCapabilities(subs);
  const assessment = capability ? normalizeAssessmentFieldsFromApi(capability) : null;
  const active = capability?.isActive !== undefined ? Boolean(capability.isActive) : true;

  const questionTotal = hasSubs
    ? subs.reduce(
        (n, s) =>
          n + activeQuestions(s.currentStateQuestions).length + activeQuestions(s.futureStateQuestions).length,
        0
      )
    : activeQuestions(capability?.currentStateQuestions).length +
      activeQuestions(capability?.futureStateQuestions).length;

  return (
    <Modal open={open} onClose={onClose} size="4xl" className="ab-view-modal" title="Capability">
      {capability ? (
        <div className="ab-view cv-view">
          <section className="ab-view__card ab-view__hero">
            <div className="ab-view__hero-top">
              <div className="ab-view__hero-main">
                <div className="ab-view__title-row">
                  <h2 className="ab-view__title">{capability.capabilityName}</h2>
                  <span
                    className={active ? "ab-view__status ab-view__status--published" : "ab-view__status"}
                  >
                    {active ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="ab-view__about cv-view__desc">
                  {capability.capabilityDescription?.trim() || "No description"}
                </p>
              </div>
              <div className="ab-view__actions">
                <Button variant="outline" onClick={onEdit}>
                  <Pencil size={16} aria-hidden />
                  Edit
                </Button>
                <Button variant="danger" onClick={onDelete}>
                  <Trash2 size={16} aria-hidden />
                  Delete
                </Button>
              </div>
            </div>
            <div className="ab-view__stats cv-view__stats">
              <div>
                <p className="ab-wizard__eyebrow">Framework</p>
                <strong className="cv-view__meta-value">{frameworkLabel || "—"}</strong>
              </div>
              <div>
                <p className="ab-wizard__eyebrow">User type</p>
                <strong className="cv-view__meta-value">{userTypeLabel || "—"}</strong>
              </div>
              <div>
                <p className="ab-wizard__eyebrow">Contents</p>
                <strong className="cv-view__meta-value">
                  {hasSubs ? `${subs.length} sub-capabilities · ` : ""}
                  {questionTotal} {questionTotal === 1 ? "question" : "questions"}
                </strong>
              </div>
            </div>
          </section>

          <div className="ab-view__grid tf-view__grid">
          <div className="cv-view__main">
          {hasSubs ? (
            subs.map((sub, index) => {
              const options = Array.isArray(sub.maturityOptions)
                ? sub.maturityOptions
                : parseMaturityOptionsJson(sub.maturityOptionsJson) ||
                  DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityOptions;
              return (
                <section key={sub.subId || sub.id || index} className="ab-view__card cv-view__sub">
                  <header className="cv-view__sub-head">
                    <span className="ab-view__qnum">{index + 1}</span>
                    <div>
                      <h3 className="cv-view__title">
                        {sub.subCapabilityName || `Sub-capability ${index + 1}`}
                        {sub.isActive === false ? (
                          <span className="ab-wizard__pill">Inactive</span>
                        ) : null}
                      </h3>
                      {sub.subCapabilityDescription?.trim() ? (
                        <p className="ab-view__about">{sub.subCapabilityDescription.trim()}</p>
                      ) : null}
                    </div>
                  </header>
                  <div className="cv-view__sub-body">
                    <Questions title="Current-state questions" questions={sub.currentStateQuestions} />
                    <Questions title="Future-state questions" questions={sub.futureStateQuestions} />
                    <Placeholders unit={sub} />
                    <Scale options={options} custom={Boolean(sub.useCustomMaturity)} />
                  </div>
                </section>
              );
            })
          ) : (
            <>
              <Questions title="Current-state questions" questions={capability.currentStateQuestions} />
              <Questions title="Future-state questions" questions={capability.futureStateQuestions} />
              <Placeholders unit={capability} />
              <Scale options={assessment?.maturityOptions} custom={Boolean(assessment?.useCustomMaturity)} />
            </>
          )}
          </div>
          <aside className="ab-view__side">
            <SourcePanel label={frameworkLabel} framework={framework} sourceTypeName={sourceTypeName} />
          </aside>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default CapabilityViewModal;
